// Pick a readable text color against an arbitrary background fill.
//
// Shape `fill` values are user-chosen (or template-chosen) and theme-
// independent — a rect explicitly set to `#ffffff` stays white in dark mode.
// That means the *text* on top of it can't follow the theme's `fgPrimary`
// (which is near-white in dark mode and would vanish on a white shape).
// Instead, we compute the text color from the fill's own luminance.
//
// The two output constants are intentionally not pulled from theme tokens:
// they describe "text that reads on a light fill" / "text that reads on a
// dark fill", which is a property of the fill, not the surrounding UI.

const TEXT_ON_LIGHT = '#18181B';
const TEXT_ON_DARK = '#FAFAFA';

// WCAG relative-luminance threshold for picking dark vs light text.
// 0.5 lands close to the perceptual midpoint and matches what most design
// systems use as a contrast cutoff for foreground-on-fill decisions.
const LUMINANCE_THRESHOLD = 0.5;

export function readableTextOn(fill: string | undefined | null): string {
  if (!fill) return TEXT_ON_LIGHT;
  const lum = relativeLuminance(fill);
  if (lum == null) return TEXT_ON_LIGHT;
  return lum > LUMINANCE_THRESHOLD ? TEXT_ON_LIGHT : TEXT_ON_DARK;
}

function relativeLuminance(color: string): number | null {
  const rgb = parseHex(color);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(srgbToLinear) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function parseHex(color: string): [number, number, number] | null {
  const trimmed = color.trim();
  if (!trimmed.startsWith('#')) return null;
  const hex = trimmed.slice(1);
  if (hex.length === 3 || hex.length === 4) {
    const r0 = hex.charAt(0);
    const g0 = hex.charAt(1);
    const b0 = hex.charAt(2);
    const r = parseInt(r0 + r0, 16);
    const g = parseInt(g0 + g0, 16);
    const b = parseInt(b0 + b0, 16);
    if ([r, g, b].some(Number.isNaN)) return null;
    return [r / 255, g / 255, b / 255];
  }
  if (hex.length === 6 || hex.length === 8) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return null;
    return [r / 255, g / 255, b / 255];
  }
  return null;
}

function srgbToLinear(c: number): number {
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
